import {
	EFxIdSdkAdapterInfoSocialCapability,
	EFxIdSdkFocusChangedReason,
	EFxIdSdkGender,
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkBaseAdapter,
	IExportedFxIdSdkMethod,
	IFxIdAddToFavoritesRequest,
	IFxIdAddToFavoritesResult,
	IFxIdJoinCommunityRequest,
	IFxIdJoinCommunityResult,
	IFxIdSdkAdapterBuyProductRequest,
	IFxIdSdkAdapterBuyProductResponse,
	IFxIdSdkAdapterGetFriendsRequest,
	IFxIdSdkAdapterInfoSocial,
	IFxIdSdkAdapterInviteFriendsRequest,
	IFxIdSdkAdapterSocialSettings,
	IFxIdSdkAdapterStatEventRequest,
	IFxIdSdkAdapterStatInitializeRequest,
	IFxIdSdkGetFriendsResult,
	IFxIdSdkGetFriendsResultFriend,
	IFxIdSdkInviteFriendsResult
} from "./FxIdSdkBaseAdapter";
import OpenApiClient from "../Api/OpenApiClient";
import {
	FxIdApplicationStoreCreatePaymentHandlerEmbeddingType,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxGamesLibWebPublicDataBase,
	FxIdWebFeaturesStoreCreatePaymentRequest
} from "../Api/gen";
import vkBridge, { EAdsFormats } from "@vkontakte/vk-bridge";
import type { ReceiveData, RequestPropsMap } from "@vkontakte/vk-bridge";
import { sharingHandlersStore, userStore } from "../Stores";
import { getAuthToken } from "@app/Api/Auth";
import { IframeType } from "@app/Screens/WebPlayer/Hooks";
import { FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus } from "../Api/gen";
import { delayPromise } from "./FxIdSdkUtils";
import { delay } from "lodash";
import { PlatformPurchaseError, runPlatformPurchaseStep } from "./PlatformPurchaseError";
import type { IPlatformPurchaseContext } from "./PlatformPurchaseError";
import { createVkBridgePurchaseDescriptor } from "./VkBridgePurchase";

/** Правила диагностики покупки в окне оплаты VK.com (#fxid-1856) */
const VK_COM_PURCHASE = createVkBridgePurchaseDescriptor("vkcom");

/**
 * Проверяет поддерживаемые VK Bridge признаки успешной покупки.
 * На Алисе result.status приходит как bool, а другие клиенты возвращают success.
 * @param result ответ окна оплаты VK
 */
function isVkComPurchaseSuccessful(result: ReceiveData<"VKWebAppShowOrderBox">): boolean {
	const response = result as unknown as { status?: string | boolean; success?: boolean };
	return response.status === "success" || response.status === true || response.success === true;
}

export class FxIdSdkAdapterForVkCom extends FxIdSdkBaseAdapter {
	readonly adapter: IframeType = "vkcom";
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = {
		...FxIdSdkAdapterSocialSettingDefault,
		ui: {
			...FxIdSdkAdapterSocialSettingDefault.ui,
			disabled: false
		}
	};
	private _adsRewardedVideoAvailable = false;
	private _adsInterstitialVideoAvailable = false;

	public static VkComDirectHost = "m.vk.com";
	private static VkComApiVersion = "5.131";
	private _vkUser?: ReceiveData<"VKWebAppGetUserInfo">;

	/** Кешированный access_token — из launch params или из VKWebAppGetAuthToken */
	private _cachedAccessToken?: string;
	/** Множество скоупов, доступных для кешированного токена */
	private _cachedScopes: Set<string> = new Set();

	constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {
		super(exportedSdk, game, config);
	}

	RegisterShareHandlers(): Promise<void> {
		const appId = this.config.PlatformData.VkontakteCom?.AppId;
		if (appId === undefined) {
			log.error("No App ID found in config. Skipping registering sharing handlers");
			return Promise.resolve();
		}
		sharingHandlersStore.getState().enableShareVk(() => {
			vkBridge
				.send("VKWebAppShare", {})
				.then((data) => {
					log.info(`Post added with: %o`, data);
				})
				.catch((error) => {
					// Ошибка
					log.info(error);
				});
		});

		return Promise.resolve();
	}

	/**
	 * Покупает товар через VK Bridge и передаёт игре диагностику сбоя шага покупки.
	 * @param request SKU товара и дополнительные параметры заказа
	 */
	async BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse> {
		const paymentRequest: FxIdWebFeaturesStoreCreatePaymentRequest = {
			Token: getAuthToken()!,
			Game: this.game,
			Sku: request.sku,
			WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom,
			EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
			ProductDescriptionHint: request.productDescriptionHint,
			ProductNameHint: request.productNameHint
		};

		if (request.developerPayload != null) {
			paymentRequest.DeveloperPayload = {
				MerchantDeveloperPayload: request.developerPayload
			};
		}

		const createPaymentResult = await runPlatformPurchaseStep(
			VK_COM_PURCHASE,
			"create_payment",
			{ sku: request.sku },
			() => OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest)
		);

		log.info("Received result from server: %o", createPaymentResult);

		const orderContext: IPlatformPurchaseContext = {
			sku: request.sku,
			transactionId: createPaymentResult.TransactionId
		};

		try {
			// У VK нет возможности отправить произвольные параметры в запрос платежа, т.ч. идентификатор заказа отправляется в коде товара
			log.info(
				`Asking vk to buy item ${createPaymentResult.OrderProduct.Sku} with public id ${createPaymentResult.TransactionId}`
			);
			const result = await runPlatformPurchaseStep(
				VK_COM_PURCHASE,
				"purchase",
				orderContext,
				() => vkBridge.send("VKWebAppShowOrderBox", {
					type: "item",
					item: createPaymentResult.TransactionId
				})
			);

			if (!isVkComPurchaseSuccessful(result)) {
				throw PlatformPurchaseError.fromError(VK_COM_PURCHASE, "purchase", orderContext, result);
			}

			await this.SetOptimisticStatus(
				createPaymentResult.TransactionId,
				FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
			);
			return {
				transactionId: createPaymentResult.TransactionId,
				stats: {
					currency: createPaymentResult.OrderProduct.Currency ?? "",
					currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
					usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
				}
			};
		} catch (error) {
			log.error("Error while buying product: %o", error);
			await this.SetOptimisticStatus(
				createPaymentResult.TransactionId,
				FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
			);
			throw error;
		}
	}

	/**
	 * Устанавливает статус заказа, не подменяя результат покупки при ошибке API.
	 * @param publicOrderId публичный идентификатор созданного заказа
	 * @param status результат покупки, известный клиенту
	 */
	private async SetOptimisticStatus(
		publicOrderId: string,
		status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus
	): Promise<void> {
		try {
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
				PublicOrderId: publicOrderId,
				Status: status
			});
		} catch (error) {
			log.error("[VKCOM] Failed to set optimistic status:", error);
		}
	}

	/** Читает access_token и скоупы из URL launch parameters (старый и новый формат VK) */
	private readLaunchParamsToken(): void {
		const params = new URLSearchParams(location.search);

		// Старый формат: access_token прямо в URL
		const accessToken = params.get("access_token");
		const accessTokenSettings = params.get("access_token_settings")
			?? params.get("vk_access_token_settings");

		if (accessToken) {
			log.info("Using access_token from launch params");
			this._cachedAccessToken = accessToken;
			if (accessTokenSettings) {
				for (const scope of accessTokenSettings.split(",")) {
					this._cachedScopes.add(scope.trim());
				}
			}
		}
	}

	/**
	 * Возвращает access_token с нужным скоупом.
	 * Сначала проверяет кеш (из launch params или предыдущего запроса),
	 * при необходимости запрашивает через VKWebAppGetAuthToken и кеширует результат.
	 */
	private async getAccessToken(requiredScope?: string): Promise<string> {
		// Если токен есть и скоуп достаточный — вернуть кешированный
		if (this._cachedAccessToken && (!requiredScope || this._cachedScopes.has(requiredScope))) {
			log.info("Using cached access token (scope: %s)", requiredScope ?? "none");
			return this._cachedAccessToken;
		}

		// Иначе запросить через VK Bridge
		const appId = this.config.PlatformData.VkontakteCom?.AppId;
		if (appId == null) throw new Error("No AppId");

		log.info("Requesting VKWebAppGetAuthToken (scope: %s)", requiredScope ?? "none");
		const result = await vkBridge.send("VKWebAppGetAuthToken", {
			app_id: appId,
			scope: requiredScope ?? ""
		});

		// Закешировать результат
		this._cachedAccessToken = result.access_token;
		if (requiredScope) {
			this._cachedScopes.add(requiredScope);
		}

		return result.access_token;
	}

	async GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial> {
		const appId = this.config.PlatformData.VkontakteCom?.AppId;
		if (appId == null) {
			throw new Error("No AppId");
		}

		await delayPromise(500);
		this._vkUser ??= await vkBridge.send("VKWebAppGetUserInfo");

		const gender = this.MapVkGender(this._vkUser.sex);

		let socialFlavor: IFxIdSdkAdapterInfoSocial["socialFlavor"] = undefined;

		const url = new URL(window.location.href);
		if (url.searchParams.has("direct") && url.searchParams.get("direct") === "true") {
			socialFlavor = "vkcom-direct";
		}

		if (socialFlavor == null) {
			const ancestorOrigins = location.ancestorOrigins;
			const url =
				ancestorOrigins == null || ancestorOrigins[0] == null
					? new URL(location.origin)
					: new URL(location.ancestorOrigins[0]);
			const host = url.host;
			if (host === FxIdSdkAdapterForVkCom.VkComDirectHost) {
				log.info("Detected vkcom direct");
				socialFlavor = "vkcom-direct";
			}
		}

		const accessToken = await this.getAccessToken();

		const vkComData: IFxIdSdkAdapterInfoSocial["vkcom"] = {
			appId,
			defaultApiVersion: FxIdSdkAdapterForVkCom.VkComApiVersion,
			defaultAccessToken: accessToken
		};

		const result: IFxIdSdkAdapterInfoSocial = {
			social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom,
			paymentsAvailable: true,
			socialFlavor,
			userId: this._vkUser.id.toString(),
			firstName: this._vkUser.first_name,
			lastName: this._vkUser.last_name,
			gender,
			photo: {
				width: 200,
				height: 200,
				url: this._vkUser.photo_200
			},
			capabilities: [
				EFxIdSdkAdapterInfoSocialCapability.FriendsList,
				EFxIdSdkAdapterInfoSocialCapability.FriendsInvite,
				EFxIdSdkAdapterInfoSocialCapability.OutgoingLinksForbidden,
				EFxIdSdkAdapterInfoSocialCapability.AdsAvailable
			],
			vkcom: vkComData
		};

		return result;
	}

	async Initialize(): Promise<void> {
		log.info("Initializing VK com adapter");
		const result = await vkBridge.send("VKWebAppInit", {});
		if (!result.result) {
			log.warn("VK Bridge init did not return result=true, readiness is verified by the profile request");
		}

		this.readLaunchParamsToken();

		const player = await this.GetSocialInfo();

		userStore.getState().updateUserId(player.userId);
		userStore.getState().updateDisplayName(`#${player.userId}`);

		void this.CheckAdsAvailable();
	}

	async CheckAdsAvailable() {
		try {
			await delayPromise(500);
			const data = await vkBridge.send("VKWebAppCheckNativeAds", { ad_format: EAdsFormats.REWARD });
			if (data.result) {
				log.info("Rewarded ads ready");
				this._adsRewardedVideoAvailable = true;
				window.FxIdSdk!.DispatchAdsVideoAvailable();
			} else {
				log.warn("Rewarded ads not found");
			}

			const dataInterstitial = await vkBridge.send("VKWebAppCheckNativeAds", {
				ad_format: EAdsFormats.INTERSTITIAL
			});
			if (dataInterstitial.result) {
				log.info("Interstitial ads ready");
				this._adsInterstitialVideoAvailable = true;
				window.FxIdSdk!.DispatchAdsInterstitialAvailable();
			} else {
				log.warn("Interstitial ads not found");
			}
		} catch (e) {
			log.error(e);
		}
	}

	StoreCurrency(): Promise<string | undefined> {
		return Promise.resolve(undefined);
	}

	StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void> {
		return Promise.resolve(undefined);
	}

	StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void> {
		return Promise.resolve(undefined);
	}

	override async AdsShowVideo() {
		try {
			const data = await vkBridge.send("VKWebAppShowNativeAds", { ad_format: EAdsFormats.REWARD });
			if (data.result) {
				log.info("Ad shown");
				window.FxIdSdk!.DispatchAdsFinished();
			} else {
				log.warn("Ad failed");
				window.FxIdSdk!.DispatchAdsFailed();
			}
		} catch (e) {
			log.error(e);
		}
	}

	override IsFocusChanged() {
		type VkFocusChangedReason = "hide" | "pip";

		const eventHandler = (e: { detail: { type: string; data: { reason: VkFocusChangedReason } | object } }) => {
			const { type, data } = e.detail;

			if (type === "VKWebAppViewHide" && "reason" in data) {
				const currentReason = (() => {
					switch (data.reason) {
						case "hide":
							return EFxIdSdkFocusChangedReason.Hint;
						case "pip":
							return EFxIdSdkFocusChangedReason.Pip;
						default:
							return EFxIdSdkFocusChangedReason.Other;
					}
				})();

				const REASON_MESSAGES: Record<EFxIdSdkFocusChangedReason, string> = {
					[EFxIdSdkFocusChangedReason.Hint]:
						"пользователь свернул мини-приложение или игру или переключился на другое приложение на мобильном устройстве",
					[EFxIdSdkFocusChangedReason.Pip]:
						"пользователь свернул мини-приложение или игру, и она работает в режиме picture-in-picture («картинка в картинке»)",
					[EFxIdSdkFocusChangedReason.Other]: "другая причина"
				};

				this.exportedSdk.DispatchFocusChanged({
					focus: false,
					reason: currentReason,
					hint: REASON_MESSAGES[currentReason] || REASON_MESSAGES[EFxIdSdkFocusChangedReason.Other]
				});
			}

			if (type === "VKWebAppViewRestore") {
				this.exportedSdk.DispatchFocusChanged({ focus: true });
			}
		};
		vkBridge.subscribe(eventHandler);
	}

	override AdsIsVideoReady() {
		void this.CheckAdsAvailable();
		return Promise.resolve(this._adsRewardedVideoAvailable);
	}

	override async GetFriends(request: IFxIdSdkAdapterGetFriendsRequest): Promise<IFxIdSdkGetFriendsResult> {
		const appId = this.config.PlatformData.VkontakteCom?.AppId;
		if (appId == null) {
			throw new Error("No AppId");
		}

		log.info("Retrieving friends from VkCom");

		const accessToken = await this.getAccessToken("friends");

		await delayPromise(500);
		const getAppFriendsResult: { response: string[] } = await vkBridge.send("VKWebAppCallAPIMethod", {
			method: "friends.getAppUsers",
			params: {
				v: FxIdSdkAdapterForVkCom.VkComApiVersion,
				access_token: accessToken
			}
		});

		log.info("getAppFriendsResult: %o", getAppFriendsResult);
		await delayPromise(500);

		const appFriendsInfo: {
			response: Array<{
				id: number;
				photo_200: string;
				first_name: string;
				last_name: string;
				sex: number;
				can_access_closed: boolean;
				is_closed: boolean;
			}>;
		} = await vkBridge.send("VKWebAppCallAPIMethod", {
			method: "users.get",
			params: {
				user_ids: getAppFriendsResult.response.join(","),
				fields: "id,first_name,last_name,sex,photo_200",
				v: FxIdSdkAdapterForVkCom.VkComApiVersion,
				access_token: accessToken
			}
		});

		log.info("appFriendsInfo: %o", appFriendsInfo);

		const result: IFxIdSdkGetFriendsResult = { friends: [] };

		for (const user of appFriendsInfo.response) {
			const gender = this.MapVkGender(user.sex);
			const friend: IFxIdSdkGetFriendsResultFriend = {
				uid: user.id.toString(),
				firstName: user.first_name,
				lastName: user.last_name,
				gender,
				photo: {
					width: 200,
					height: 200,
					url: user.photo_200
				}
			};

			result.friends.push(friend);
		}

		return result;
	}

	private MapVkGender(sex: number) {
		let gender: EFxIdSdkGender = EFxIdSdkGender.Unspecified;
		switch (sex) {
			case 1:
				gender = EFxIdSdkGender.Female;
				break;
			case 2:
				gender = EFxIdSdkGender.Male;
				break;
		}

		return gender;
	}

	override async InviteFriends(request: IFxIdSdkAdapterInviteFriendsRequest): Promise<IFxIdSdkInviteFriendsResult> {
		const data = await vkBridge.send("VKWebAppShowInviteBox", { request_id: request.trackString });

		return { friends: [] };
	}

	override async JoinCommunity(request: IFxIdJoinCommunityRequest): Promise<IFxIdJoinCommunityResult> {
		try {
			const data = await vkBridge.send("VKWebAppJoinGroup", { group_id: parseInt(request.community_id) });
			return { success: data.result };
		} catch (err: unknown) {
			log.error(err);
		}

		return { success: false };
	}

	override async AddToFavorites(request: IFxIdAddToFavoritesRequest): Promise<IFxIdAddToFavoritesResult> {
		try {
			const data = await vkBridge.send("VKWebAppAddToFavorites");
			return { success: data.result };
		} catch (err: unknown) {
			log.error(err);
		}

		return { success: false };
	}

	public async SendVkBridge(method: string, props: any) {
		return await vkBridge.send(method as keyof RequestPropsMap, props);
	}

	override AdsIsInterstitialReady() {
		return Promise.resolve(this._adsInterstitialVideoAvailable);
	}

	override async AdsShowInterstitial() {
		try {
			const data = await vkBridge.send("VKWebAppShowNativeAds", { ad_format: EAdsFormats.INTERSTITIAL });
			if (data.result) {
				log.info("Ad shown");
				window.FxIdSdk!.DispatchAdsFinished();
			} else {
				log.warn("Ad failed");
				window.FxIdSdk!.DispatchAdsFailed();
			}
		} catch (e) {
			log.error(e);
		}
		return Promise.resolve();
	}
}
